// HealingTone432 — a continuous 432 Hz-based ambient pad synthesized in
// the browser (no audio files, no bandwidth, no permissions).
//
// The 432 Hz "healing" frequency is layered with its fifth (648 Hz) and
// its octave-below sub (216 Hz), routed through a warm low-pass filter
// so the pad reads as gentle music rather than a sine tone. Two very
// slow LFOs shape it:
//   • an amplitude LFO gives the pad a natural breathing quality,
//   • a filter LFO opens and closes the timbre so the sound never feels
//     static or synthetic.
//
// This is a distinct pad from `breathAmbience.js`, which is a warmer
// nervous-system-drop 216/324/108 Hz pad. This one sits at 432 Hz
// because the Loving-Kindness practice specifically calls for it.

let _ctx = null;
let _instance = null;

function ctx() {
  if (_ctx) return _ctx;
  try {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return null;
    _ctx = new C();
    return _ctx;
  } catch { return null; }
}

/**
 * Start the continuous 432 Hz pad. Idempotent — calling it while a pad
 * is already running just returns the existing instance.
 */
export function startHealingTone432({
  peakVolume = 0.05,
  breathSeconds = 12,
} = {}) {
  if (_instance) return _instance;
  const c = ctx();
  if (!c) return null;
  try {
    if (c.state === "suspended") c.resume().catch(() => {});
    const now = c.currentTime;

    // Master gain — starts at 0 for a smooth fade-in.
    const master = c.createGain();
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(peakVolume, now + 2.8);

    // Warm low-pass filter with a slow LFO on the cutoff.
    const filter = c.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(820, now);
    filter.Q.setValueAtTime(0.75, now);

    const filterLfo = c.createOscillator();
    const filterLfoGain = c.createGain();
    filterLfo.type = "sine";
    filterLfo.frequency.setValueAtTime(1 / 28, now);
    filterLfoGain.gain.setValueAtTime(210, now);
    filterLfo.connect(filterLfoGain);
    filterLfoGain.connect(filter.frequency);
    filterLfo.start(now);

    // Three-voice pad — root 432 Hz, fifth 648 Hz, sub 216 Hz.
    // Slight detune on the first two so the pad has a gentle chorus.
    const voices = [
      { freq: 432, type: "sine",     gain: 0.55, detune: -4 },
      { freq: 648, type: "sine",     gain: 0.32, detune: 5 },
      { freq: 216, type: "triangle", gain: 0.30, detune: 0 },
    ];

    const voiceNodes = voices.map((v) => {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = v.type;
      osc.frequency.setValueAtTime(v.freq, now);
      osc.detune.setValueAtTime(v.detune, now);
      g.gain.setValueAtTime(v.gain, now);
      osc.connect(g);
      g.connect(filter);
      osc.start(now);
      return { osc, g };
    });

    // Breath LFO — modulates the master gain so the pad softly rises
    // and falls without dropping to silence between breaths.
    const breathLfo = c.createOscillator();
    const breathLfoDepth = c.createGain();
    breathLfo.type = "sine";
    breathLfo.frequency.setValueAtTime(1 / breathSeconds, now);
    breathLfoDepth.gain.setValueAtTime(peakVolume * 0.32, now);
    breathLfo.connect(breathLfoDepth);
    breathLfoDepth.connect(master.gain);
    breathLfo.start(now);

    filter.connect(master);
    master.connect(c.destination);

    _instance = {
      context: c,
      master, filter,
      filterLfo, filterLfoGain,
      breathLfo, breathLfoDepth,
      voiceNodes,
      peakVolume,
    };
    return _instance;
  } catch {
    return null;
  }
}

/** Fade the pad out and free the audio nodes. */
export function stopHealingTone432({ fadeSeconds = 1.8 } = {}) {
  const inst = _instance;
  if (!inst) return;
  _instance = null;
  const c = inst.context;
  try {
    const now = c.currentTime;
    try { inst.breathLfoDepth.gain.setValueAtTime(0, now); } catch { /* noop */ }
    inst.master.gain.cancelScheduledValues(now);
    inst.master.gain.setValueAtTime(Math.max(inst.master.gain.value, 0.0001), now);
    inst.master.gain.linearRampToValueAtTime(0, now + fadeSeconds);
    const stopAt = now + fadeSeconds + 0.1;
    inst.voiceNodes.forEach(({ osc }) => { try { osc.stop(stopAt); } catch { /* noop */ } });
    try { inst.filterLfo.stop(stopAt); } catch { /* noop */ }
    try { inst.breathLfo.stop(stopAt); } catch { /* noop */ }
    setTimeout(() => {
      try { inst.master.disconnect(); } catch { /* noop */ }
      try { inst.filter.disconnect(); } catch { /* noop */ }
    }, (fadeSeconds + 0.3) * 1000);
  } catch { /* silent */ }
}

/** Temporarily reduce (or restore) the pad's volume. */
export function duckHealingTone432(level = 0.35, seconds = 0.6) {
  const inst = _instance;
  if (!inst) return;
  try {
    const c = inst.context;
    const now = c.currentTime;
    const target = Math.max(0, Math.min(1, level)) * inst.peakVolume;
    inst.master.gain.cancelScheduledValues(now);
    inst.master.gain.setValueAtTime(Math.max(inst.master.gain.value, 0.0001), now);
    inst.master.gain.linearRampToValueAtTime(target, now + seconds);
  } catch { /* noop */ }
}

export function isHealingTone432Playing() {
  return !!_instance;
}
